import { Band, BandStack } from '@/components/ui/Band'
import { ButtonLink } from '@/components/ui/Button'
import { PageHero } from '@/components/ui/PageHero'
import { Eyebrow, SourceRef } from '@/components/ui/Section'
import { IconBadge } from '@/components/ui/Icons'
import { ScrollRail } from '@/components/motion/ScrollRail'
import { Stagger } from '@/components/motion/Reveal'
import { useDocumentMeta } from '@/lib/seo'
import { ROUTES } from '@/lib/site'
import { BRIDGED_CAPABILITIES, METHOD_STAGES } from '@/content/method'

const TOTAL_LAYERS = 4

export default function Method() {
  useDocumentMeta(ROUTES.method)

  return (
    <BandStack>
      <Band layer={1} total={TOTAL_LAYERS} tone="ice" routeTab="method">
        <PageHero
          crumb="How it works"
          title="Between your export and a panel there are eight stages, and none of them throw anything away."
          lede="A spreadsheet written for a human reader is not a dataset. It has a title block, merged cells, subtotals in the middle, a scale note in prose and a fiscal calendar that is not January to December. Every stage below exists because one of those things breaks a naive read, and each stage records what it did so a figure can be walked back to the cell it came from."
        />
      </Band>

      <Band layer={2} total={TOTAL_LAYERS} tone="warm" labelledBy="stages-heading">
        <h2 id="stages-heading" className="sr-only">
          The eight stages
        </h2>
        <ScrollRail
          steps={METHOD_STAGES.map((stage, index) => ({
            key: stage.name,
            badge: String(index + 1).padStart(2, '0'),
            content: (
              <div className="card-spot grid gap-6 rounded-card border border-line bg-white/85 p-6 shadow-card backdrop-blur sm:p-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-10">
                <div>
                  <div className="flex items-center gap-4">
                    <IconBadge name={stage.icon} />
                    <Eyebrow className="text-primary">{stage.stage}</Eyebrow>
                  </div>
                  <h3 className="mt-4 text-[22px] font-extrabold tracking-[-0.02em] sm:text-[26px]">
                    {stage.name}
                  </h3>
                  <p className="mt-4 max-w-measure text-[14.5px] leading-[1.75] text-muted">
                    {stage.body}
                  </p>
                </div>
                <div className="rounded-card border border-line bg-surface-2/70 p-5 sm:p-6">
                  <p className="text-[13.5px] leading-[1.75] text-muted">{stage.note}</p>
                </div>
              </div>
            ),
          }))}
        />
      </Band>

      <Band layer={3} total={TOTAL_LAYERS} tone="ice" labelledBy="seam-heading">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Eyebrow className="text-primary">The engine seam</Eyebrow>
            <h2
              id="seam-heading"
              className="mt-6 text-balance text-[28px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[36px]"
            >
              One module touches the engine. Every import inside it is function-local.
            </h2>
            <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
              The forecasting engine is vendored as a read-only mirror of the platform monorepo, not
              forked. A bug found in it is fixed upstream and pulled down; an in-place edit fails a
              blocking CI gate. Deliberate divergence is allowed, but it has to be declared and
              moved out of the mirror.
            </p>
            <p className="mt-4 max-w-measure text-[15px] leading-[1.75] text-muted">
              Nothing in the product imports the engine at module level, so starting the API does
              not load a machine-learning stack. That is enforced three ways, including an import
              test run with the vendored tree renamed away.
            </p>
          </div>
          <div className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card sm:p-8">
            <Eyebrow>Bridged capabilities</Eyebrow>
            <Stagger variant="left" step={80} as="ul" itemAs="li" className="mt-5 grid gap-4">
              {BRIDGED_CAPABILITIES.map((capability) => (
                <div
                  key={capability.name}
                  className="border-b border-line pb-4 last:border-b-0 last:pb-0"
                >
                  <strong className="block text-[14.5px] font-bold tracking-[-0.01em]">
                    {capability.name}
                  </strong>
                  <span className="mt-1.5 block text-[13.5px] leading-[1.7] text-muted">
                    {capability.detail}
                  </span>
                </div>
              ))}
            </Stagger>
            <div className="mt-5 border-t border-line pt-4">
              <SourceRef>bi/engine_bridge.py</SourceRef>
            </div>
          </div>
        </div>
      </Band>

      <Band layer={4} total={TOTAL_LAYERS} tone="mint" labelledBy="next-heading" showMark={false}>
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="next-heading"
            className="text-balance text-[26px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[34px]"
          >
            Next: what each figure carries with it.
          </h2>
          <p className="mx-auto mt-5 max-w-[58ch] text-pretty text-[15px] leading-[1.75] text-muted">
            Provenance, formula, drivers, line items and sources — and the five-stage trace that
            puts them in order.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <ButtonLink to={ROUTES.provenance.path} size="lg">
              Provenance and trace
            </ButtonLink>
            <ButtonLink to={ROUTES.projection.path} variant="secondary" size="lg">
              How projections work
            </ButtonLink>
          </div>
        </div>
      </Band>
    </BandStack>
  )
}
